import { useEffect, useState, useCallback } from 'react';
import { FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import { authFetch } from '../auth';

const headerRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  gap: 16,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  backgroundColor: 'var(--card-bg)',
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 8px',
  borderBottom: '2px solid var(--border)',
  backgroundColor: 'var(--table-head-bg)',
  fontWeight: 600,
  fontSize: 14,
};

const tdStyle = {
  padding: '10px 8px',
  borderBottom: '1px solid var(--border)',
  fontSize: 13,
};

const actionBtnRow = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
};

const emptyState = {
  textAlign: 'center',
  padding: '24px 8px',
  color: 'var(--text-muted, #666)',
  fontStyle: 'italic',
};

const formatSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let value = bytes;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch (_) {
    return '—';
  }
};

export default function TrashView({ API_URL, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null); // { type, id, name }
  const [actionBusy, setActionBusy] = useState(false);

  const toast = useCallback((message, type = 'info', duration = 3000) => {
    try {
      if (window.spartaToast) {
        window.spartaToast(message, type, duration);
      }
    } catch (_) {}
  }, []);

  const triggerChange = useCallback(async () => {
    if (typeof onChange === 'function') {
      try { await onChange(); } catch (_) {}
    }
  }, [onChange]);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_URL}/files/trash`);
      const body = await res.json().catch(() => ([]));
      if (!res.ok) {
        throw new Error(body?.message || 'Failed to load trash');
      }
      setItems(Array.isArray(body) ? body : []);
    } catch (e) {
      setError(e.message || 'Failed to load trash');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const closeConfirm = () => setPending(null);

  const restoreItem = async (id) => {
    setActionBusy(true);
    try {
      const res = await authFetch(`${API_URL}/files/trash/${encodeURIComponent(id)}/restore`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok !== true) {
        throw new Error(body?.message || 'Failed to restore file');
      }
      toast(`Restored ${body.filename || 'file'}`, 'success', 2500);
      await loadTrash();
      await triggerChange();
    } catch (e) {
      toast(e.message || 'Failed to restore file', 'error', 3500);
    } finally {
      setActionBusy(false);
    }
  };

  const deleteItem = async (id) => {
    setActionBusy(true);
    try {
      const res = await authFetch(`${API_URL}/files/trash/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok !== true) {
        throw new Error(body?.message || 'Failed to remove file');
      }
      toast(`Removed ${body.filename || 'file'} from trash`, 'success', 2500);
      await loadTrash();
    } catch (e) {
      toast(e.message || 'Failed to remove file', 'error', 3500);
    } finally {
      setActionBusy(false);
    }
  };

  const emptyTrash = async () => {
    setActionBusy(true);
    try {
      const res = await authFetch(`${API_URL}/files/trash`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok !== true) {
        throw new Error(body?.message || 'Failed to empty trash');
      }
      toast(`Emptied trash (${body.removed || 0} file${(body.removed || 0) === 1 ? '' : 's'})`, 'success', 2500);
      await loadTrash();
    } catch (e) {
      toast(e.message || 'Failed to empty trash', 'error', 3500);
    } finally {
      setActionBusy(false);
    }
  };

  let confirmTitle = 'Confirm';
  let confirmMessage = '';
  let confirmLabel = 'Confirm';
  if (pending) {
    if (pending.type === 'restore') {
      confirmTitle = 'Restore File';
      confirmMessage = `Restore "${pending.name}" to the active cabinet?`;
      confirmLabel = 'Restore';
    } else if (pending.type === 'delete') {
      confirmTitle = 'Delete Permanently';
      confirmMessage = `Permanently delete "${pending.name}"? This cannot be undone.`;
      confirmLabel = 'Delete';
    } else if (pending.type === 'empty') {
      confirmTitle = 'Empty Trash';
      confirmMessage = 'Permanently remove all files from the trash? This cannot be undone.';
      confirmLabel = 'Empty Trash';
    }
  }

  return (
    <div>
      <div style={headerRow}>
        <div>
          <h2 style={{ margin: 0 }}>Trash</h2>
          <p style={{ margin: '6px 0 0 0', color: 'var(--text-muted, #666)', fontSize: 13 }}>
            Recently deleted files can be restored here. Emptying the trash permanently removes them.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={loadTrash} disabled={loading || actionBusy}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            className="btn btn-delete"
            onClick={() => setPending({ type: 'empty' })}
            disabled={items.length === 0 || loading || actionBusy}
          >
            Empty Trash
          </button>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 12, color: '#b71c1c', fontWeight: 600 }}>
          {error}
        </div>
      )}
      <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '35%' }}>File</th>
              <th style={thStyle}>Size</th>
              <th style={thStyle}>Deleted</th>
              <th style={{ ...thStyle, width: '20%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!items || items.length === 0) && (
              <tr>
                <td style={{ ...tdStyle, ...emptyState }} colSpan={4}>
                  {loading ? 'Loading…' : 'Trash is empty'}
                </td>
              </tr>
            )}
            {items.map(item => (
              <tr key={item.id}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{item.filename}</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Original created: {formatDate(item.createdAt)}</div>
                </td>
                <td style={tdStyle}>{formatSize(item.size || 0)}</td>
                <td style={tdStyle}>{formatDate(item.deletedAt)}</td>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                  <div style={actionBtnRow}>
                    <button
                      className="btn btn-primary"
                      disabled={actionBusy}
                      onClick={() => setPending({ type: 'restore', id: item.id, name: item.filename })}
                      title="Restore"
                    >
                      <FiRotateCcw aria-hidden style={{ marginRight: 4 }} />
                      Restore
                    </button>
                    <button
                      className="btn btn-delete"
                      disabled={actionBusy}
                      onClick={() => setPending({ type: 'delete', id: item.id, name: item.filename })}
                      title="Delete permanently"
                    >
                      <FiTrash2 aria-hidden style={{ marginRight: 4 }} />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pending && (
        <ConfirmModal
          open={!!pending}
          title={confirmTitle}
          message={confirmMessage}
          confirmLabel={confirmLabel}
          onCancel={closeConfirm}
          onConfirm={async () => {
            const current = pending;
            closeConfirm();
            if (!current) return;
            if (current.type === 'restore') {
              await restoreItem(current.id);
            } else if (current.type === 'delete') {
              await deleteItem(current.id);
            } else if (current.type === 'empty') {
              await emptyTrash();
            }
          }}
        />
      )}
    </div>
  );
}
